import { ref, onBeforeUnmount } from 'vue'

/**
 * 扫码支付的「轮询 + 关单」骨架。
 *
 * 背景：PublicPluginDetail / PluginPricing / UserProfilePlugins
 *       三处各写了一遍"setInterval 轮询 /pay/pollStatus + 关单"的同构逻辑。
 *
 * ⚠️ 设计上刻意只抽"相同的那部分"：
 *   - 下单请求各页面的 URL 与参数都不同 ⇒ 由调用方传入 createOrder；
 *   - 二维码过期机制三页各不相同（轮询内判断 / setTimeout / 独立 interval），
 *     强行统一会改变行为 ⇒ 过期逻辑保留在调用方，通过 onTick 时机回调通知；
 *   - 轮询间隔三处原值都是 3000，但做成参数以便个别页面调整。
 *
 * ⚠️ 所有网络请求都由调用方注入，本文件不 import request.js。
 *    原因：ExePricing 需要带 exe 自己的 JWT，不能用全局实例。
 *
 * @param {Function} createOrder   (extra) => Promise<{ qrCodeUrl, outTradeNo, timeExpire, orderId }>
 * @param {Function} pollStatusApi (outTradeNo) => Promise<{ success, paid }>
 * @param {Function} closeOrderApi (outTradeNo) => Promise<any>
 * @param {Function} onSuccess     (res) => void   支付成功后的页面动作
 * @param {Function} onFail        (err) => void   轮询出错时的处理（不中断轮询）
 * @param {Function} onTick        () => boolean   每次轮询前调用；返回 true 表示已过期需停止
 * @param {number}   intervalMs    轮询间隔，默认 3000（与三处原值一致）
 * @param {object}   timerRef      可选。传入一个 ref，让调用方能读到/清理自己的定时器句柄。
 *                                 用于那种"文件里已有多处 clearInterval(xxxInterval)"的页面，
 *                                 不必为了接入本 composable 而重写那些清理逻辑。
 */
export function useQrPayment({
    createOrder,
    pollStatusApi,
    closeOrderApi,
    onSuccess,
    onFail,
    onTick,
    intervalMs = 3000,
    timerRef = null,
}) {
    const paymentStatus = ref('pending') // pending | success | expired | closed
    const currentOrderNo = ref('')
    const currentOrderId = ref(null)

    let pollTimer = null

    const stopPolling = () => {
        if (pollTimer) {
            clearInterval(pollTimer)
            pollTimer = null
        }
        if (timerRef) timerRef.value = null
    }

    /** 由调用方在拿到 createOrder 的返回后调用，开始轮询 */
    const startPolling = (outTradeNo) => {
        stopPolling()
        pollTimer = setInterval(async () => {
            if (timerRef) timerRef.value = pollTimer
            // 过期判断交给调用方（三页机制不同，强行统一会改行为）
            if (onTick && onTick() === true) {
                stopPolling()
                paymentStatus.value = 'expired'
                return
            }
            try {
                const res = await pollStatusApi(outTradeNo)
                if (res && res.success && res.paid) {
                    stopPolling()
                    paymentStatus.value = 'success'
                    onSuccess && onSuccess(res)
                }
            } catch (e) {
                // 与原实现一致：单次轮询失败只记日志，不中断轮询
                onFail && onFail(e)
            }
        }, intervalMs)
        if (timerRef) timerRef.value = pollTimer
    }

    /** 由调用方在拿到订单信息后调用，记录当前订单 */
    const trackOrder = (outTradeNo, orderId = null) => {
        currentOrderNo.value = outTradeNo || ''
        currentOrderId.value = orderId
    }

    /** 关单：调用方可传入指定单号，不传则用当前记录的 */
    const closeOrder = async (outTradeNo = null) => {
        stopPolling()
        const no = outTradeNo || currentOrderNo.value
        if (!no) return
        await closeOrderApi(no)
        paymentStatus.value = 'closed'
    }

    const reset = () => {
        stopPolling()
        paymentStatus.value = 'pending'
        currentOrderNo.value = ''
        currentOrderId.value = null
    }

    // 组件卸载必须清 timer（现有三处都缺这个保护，属结构修复）
    onBeforeUnmount(stopPolling)

    return {
        paymentStatus,
        currentOrderNo,
        currentOrderId,
        startPolling,
        stopPolling,
        trackOrder,
        closeOrder,
        reset,
    }
}